export function beginContentReadyDetection(
  fireworkNode: HTMLElement,
  setIsLoading: (v: boolean) => void,
  contentObserverRef: React.MutableRefObject<MutationObserver | null>,
  contentTimeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>
) {
  if (fireworkNode.childNodes.length > 0) {
    setIsLoading(false);
    return;
  }

  contentObserverRef.current = new MutationObserver(() => {
    if (fireworkNode.childNodes.length > 0) {
      setIsLoading(false);
      contentObserverRef.current?.disconnect();
      contentObserverRef.current = null;
      if (contentTimeoutRef.current) {
        clearTimeout(contentTimeoutRef.current);
        contentTimeoutRef.current = null;
      }
    }
  });

  contentObserverRef.current.observe(fireworkNode, {
    childList: true,
    subtree: true,
  });

  contentTimeoutRef.current = setTimeout(() => {
    setIsLoading(false);
    if (contentObserverRef.current) {
      contentObserverRef.current.disconnect();
      contentObserverRef.current = null;
    }
    contentTimeoutRef.current = null;
  }, 3000);
}

export function setupFireworkDiscovery(args: {
  storyBlockRef: React.RefObject<HTMLDivElement | null>;
  fireworkNodeRef: React.MutableRefObject<HTMLElement | null>;
  playlist?: string;
  videoId?: string;
  beginContentReadyDetection?: (el: HTMLElement) => void;
  addEventListeners: (el: HTMLElement) => void;
  checkElementIntervalRef: React.MutableRefObject<ReturnType<typeof setInterval> | null>;
  timeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
  origin?: string;
}) {
  const {
    storyBlockRef,
    fireworkNodeRef,
    playlist,
    videoId,
    beginContentReadyDetection,
    addEventListeners,
    checkElementIntervalRef,
    timeoutRef,
    origin,
  } = args;

  const findScopedFireworkNode = (): HTMLElement | null => {
    const scopedNode = storyBlockRef.current?.querySelector<HTMLElement>("fw-storyblock") ?? null;
    if (scopedNode) return scopedNode;

    if (videoId) {
      const byVideoId = document.querySelector<HTMLElement>(`fw-storyblock[videoid="${videoId}"]`);
      if (byVideoId) return byVideoId;
    }

    if (playlist) {
      const byPlaylist = document.querySelector<HTMLElement>(
        `fw-storyblock[playlist="${playlist}"]`
      );
      if (byPlaylist) return byPlaylist;
    }

    return document.querySelector<HTMLElement>("fw-storyblock");
  };

  checkElementIntervalRef.current = setInterval(() => {
    const fireworkNode = findScopedFireworkNode();

    if (fireworkNode) {
      if (checkElementIntervalRef.current) {
        clearInterval(checkElementIntervalRef.current as unknown as number);
        checkElementIntervalRef.current = null;
      }

      fireworkNodeRef.current = fireworkNode;
      addEventListeners(fireworkNode);
      if (origin === "influencers" && beginContentReadyDetection) {
        beginContentReadyDetection(fireworkNode);
      }
    }
  }, 100);

  timeoutRef.current = setTimeout(() => {
    if (checkElementIntervalRef.current) {
      clearInterval(checkElementIntervalRef.current as unknown as number);
      checkElementIntervalRef.current = null;
    }
  }, 10000);

  return () => {
    if (checkElementIntervalRef.current) {
      clearInterval(checkElementIntervalRef.current as unknown as number);
      checkElementIntervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current as unknown as number);
      timeoutRef.current = null;
    }
  };
}
